import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  EditableToken,
  RichTextContent,
  useContentToken,
  useEditMode,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage, cn } from '@vibesites/ui-primitives';
import { btnOutlineOnPrimary, hasValue, linkTarget } from '../utils/content';

const ctaDef: SlotGroupDefinition = {
  label: 'Callout Button',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'Learn More' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

/** Rust brand-promise band with an optional badge/logo graphic on the left. */
export function CalloutBand() {
  const editMode = useEditMode();
  const badge = useContentToken('calloutband.badge');
  const heading = useContentToken('calloutband.heading');
  const cta = useSlotGroup('calloutband.cta', ctaDef);

  if (!hasValue(heading) && !editMode?.isEditMode) return null;
  const hasBadge = hasValue(badge);

  return (
    <section className="bg-primary text-[color:var(--vibe-color-text-on-primary)]">
      <div
        className={cn(
          'mx-auto grid max-w-[1180px] items-center gap-8 px-6 py-14 text-center md:py-16 lg:px-10',
          (hasBadge || editMode?.isEditMode) && 'md:grid-cols-[minmax(0,1fr)_minmax(0,3fr)] md:gap-12 md:text-left',
        )}
      >
        {(hasBadge || editMode?.isEditMode) && (
          <EditableToken path="calloutband.badge" type="image" as="div" className="js-reveal-zoom mx-auto w-full max-w-[260px]">
            {hasBadge ? (
              <VibesitesImage url={badge || ''} alt="" sizes="260px" className="h-auto w-full object-contain" />
            ) : (
              <div className="flex aspect-[3/2] w-full items-center justify-center border border-dashed border-[color:var(--vibe-color-text-on-primary)] font-body text-sm opacity-70">
                Optional badge or logo
              </div>
            )}
          </EditableToken>
        )}
        <div>
          <EditableToken path="calloutband.heading" as="h2" className="js-reveal js-heading text-[30px] sm:text-[36px] lg:text-[42px]">
            {heading}
          </EditableToken>
          <RichTextContent
            path="calloutband.body"
            as="div"
            className={cn('js-reveal js-d1 js-copy mx-auto mt-4 max-w-[560px] !text-[17px] !leading-[1.75]', (hasBadge || editMode?.isEditMode) && 'md:mx-0')}
          />
          <EditableSlotGroup groupPath="calloutband.cta" definition={ctaDef} className="js-reveal js-d2">
            <a href={cta.url || '#'} target={linkTarget(cta.url, cta._target)} className={`${btnOutlineOnPrimary} mt-7 min-w-[196px] !font-semibold`}>
              {cta.label || 'Learn More'}
            </a>
          </EditableSlotGroup>
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'CalloutBand',
  displayName: 'Callout Band',
  tokens: {
    'calloutband.badge': {
      label: 'Badge / Logo (optional)',
      type: 'image',
      defaultValue: '',
      description: 'A white program logo or badge shown left of the heading. Leave empty to center the copy.',
    },
    'calloutband.heading': {
      label: 'Heading',
      type: 'text',
      defaultValue: 'Know Before You Lease',
      description: 'Clear the heading to hide this band.',
      generator: {
        prompt: 'A short headline naming one real leasing promise or resident perk this community offers (pricing transparency, specials, perks program). Leave empty if nothing distinctive is on record.',
        context: 'Rust callout band on the home and floor plans pages, right after the welcome intro.',
        length: '2–5 words',
      },
    },
    'calloutband.body': {
      label: 'Body',
      type: 'richtext',
      defaultValue:
        '<p>Explore pricing, availability, and what is included up front, so you can plan with confidence and feel right at home.</p>',
      generator: {
        prompt: 'One or two sentences expanding on the callout heading, grounded in what this community actually offers.',
        context: 'Body under the callout band heading, above a "Learn More" button.',
        length: '1–2 sentences',
      },
    },
  },
  slotGroups: {
    'calloutband.cta': ctaDef,
  },
};

(CalloutBand as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default CalloutBand;
